import { CommonModule } from '@angular/common';
import { Component, EnvironmentInjector, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ModalService } from '@indice/ng-components';
import { TranslocoDirective } from '@jsverse/transloco';
import { mergeMap, of, take, tap } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { UnikoServiceTypes } from 'src/app/services/payments-api.service';
import { ServiceCardComponent } from 'src/app/shared/components/service-card/service-card.component';
import { SaveSearchAnonymousModalComponent } from '../save-search-modal/save-search-anonymous-modal/save-search-anonymous-modal.component';
import { UnavailableServiceComponent } from '../../shared/components/unavailable-service/unavailable-service.component';
import { localizeRoute } from 'src/app/shared/utilities/localizeRoutes';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';

@Component({
  selector: 'app-services',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoDirective,
    ServiceCardComponent,
    LanguageRouterLinkDirective,
  ],
  templateUrl: './services.component.html',
  styleUrl: './services.component.css',
})
export class ServicesComponent implements OnInit {
  selectedCategory: string = 'buyers'; // Initial state
  clientCategories = [
    { id: 'buyers', label: 'ourServices.forBuyers' },
    {
      id: 'sellers',
      label: 'ourServices.forSellers',
    },
  ];

  public readonly commingSoonServiceData = [
    {
      type: UnikoServiceTypes.undefined,
      img: '../../../../assets/img/contract_support_1.png',
      title: 'contractSupport.services.title',
      description: 'contractSupport.services.description',
      ctaLabel: 'more',
      comingSoon: true,
    },
    {
      type: UnikoServiceTypes.undefined,
      img: '../../../../assets/img/house_id_1.png',
      title: 'digitalId.services.title',
      description: 'digitalId.services.description',
      ctaLabel: 'more',
      comingSoon: true,
    },
    {
      type: UnikoServiceTypes.undefined,
      img: '../../../../assets/img/house-keys.png',
      title: 'transferDelivery.services.title',
      description: 'transferDelivery.services.description',
      ctaLabel: 'more',
      comingSoon: true,
    },
  ];
  public readonly buyersServiceData = [
    {
      type: UnikoServiceTypes.legalReport,
      img: '../../../../assets/img/business_search.png',
      title: 'preApproval.services.title3',
      description: 'preApproval.services.description3',
      ctaLabel: 'more',
      comingSoon: false,
    },
    {
      type: UnikoServiceTypes.technicalReport,
      img: '../../../../assets/img/file_property.png',
      title: 'preApproval.services.title2',
      description: 'preApproval.services.description2',
      ctaLabel: 'more',
      comingSoon: false,
    },
    {
      type: UnikoServiceTypes.biddingSupport,
      img: '../../../../assets/img/file_property.png',
      title: 'bidding.services.title',
      description: 'bidding.services.description',
      ctaLabel: 'more',
      comingSoon: false,
    },
    ...this.commingSoonServiceData,
  ];
  public readonly sellersServiceData: any[] = [
    this.buyersServiceData[0],
    this.buyersServiceData[1],
    {
      type: UnikoServiceTypes.photography,
      img: '../../../../assets/img/camera.png',
      title: 'photography.services.title1',
      description: 'photography.services.description1',
      ctaLabel: 'more',
    },
    {
      type: UnikoServiceTypes.propertyFileConstitution,
      img: '../../../../assets/img/service-folder-2-1.png',
      title: 'preApproval.services.title4',
      description: 'preApproval.services.description4',
      ctaLabel: 'more',
    },
    this.commingSoonServiceData[0],
    this.commingSoonServiceData[1],
  ];
  public readonly buyersOtherServiceData = [
    {
      type: UnikoServiceTypes.undefined,
      img: '../../../../assets/img/CLEANING.png',
      title: 'cleaning.services.title',
      description: undefined,
      ctaLabel: 'more',
      comingSoon: true,
    },
    {
      type: UnikoServiceTypes.undefined,
      img: '../../../../assets/img/MOVING.png',
      title: 'moving.services.title',
      description: undefined,
      ctaLabel: 'more',
      comingSoon: true,
    },
    {
      type: UnikoServiceTypes.undefined,
      img: '../../../../assets/img/TOOLS.png',
      title: 'renovation.services.title',
      description: undefined,
      ctaLabel: 'more',
      comingSoon: true,
    },
  ];
  public readonly sellersOtherServiceData = [
    this.buyersOtherServiceData[0],
    this.buyersOtherServiceData[1],
  ];

  protected unikoServiceTypeEnum = UnikoServiceTypes;
  protected propertyCode: string | undefined;
  protected isLoggedIn: boolean = false;

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _modalService: ModalService,
    private authService: AuthService,
    @Inject(EnvironmentInjector) private envInjector: EnvironmentInjector,
  ) {
    this.propertyCode = this._activatedRoute.snapshot.queryParams['property'];

    this.authService
      .isLoggedIn()
      .pipe(tap((t: boolean) => (this.isLoggedIn = t)))
      .subscribe();
  }

  ngOnInit(): void {
    // Load the saved option from localStorage (if available)
    const savedOption = localStorage.getItem('services.selectedOption');
    if (savedOption) {
      this.selectedCategory = savedOption;
    }
  }

  toggleSwitch(id: string) {
    this.selectedCategory = id;
    // # Save the selected option to localStorage to preserve the user's choice when they return.
    localStorage.setItem('services.selectedOption', this.selectedCategory);
  }

  reload(serviceType: string) {
    const serviceTypeKey = serviceType as keyof typeof UnikoServiceTypes;
    this._router.navigate([
      localizeRoute(`/services/${serviceTypeKey}`, this.envInjector),
    ]);
  }

  unavailableService(type: any, event: Event) {
    if (
      type == UnikoServiceTypes.legalReport ||
      type == UnikoServiceTypes.technicalReport
    ) {
      const unavailableService = this._modalService.show(
        UnavailableServiceComponent,
        {
          keyboard: true,
          class: 'h-dvh md:h-auto',
        },
      );

      // unavailableService.onHidden?.pipe(take(1), mergeMap((r: any) => {
      //   return of();
      // })).subscribe()
    }
  }

  protected goToServicePage(serviceType: UnikoServiceTypes): void {
    if (!this.isLoggedIn) {
      this._modalService.show(SaveSearchAnonymousModalComponent, {
        keyboard: true,
        class: 'h-dvh md:h-auto',
      });

      return;
    }

    const serviceTypeString = UnikoServiceTypes[serviceType];
    if (serviceTypeString) {
      this._router.navigate(
        [localizeRoute(`/services/${serviceTypeString}`, this.envInjector)],
        {
          queryParams: {
            property: this.propertyCode,
          },
          queryParamsHandling: 'merge',
        },
      );
    } else {
      console.error('service type not found');
    }
  }
}
